/**
 * Chart Tick Configuration Utility
 * 
 * Provides responsive tick label configuration for chart components
 * to handle crowded X-axis labels by rotating and adjusting spacing
 * based on the number of data points.
 */

export interface TickConfig {
  angle: number;
  textAnchor: 'start' | 'middle' | 'end';
  fontSize: number;
  interval: number | 'preserveStart' | 'preserveEnd' | 'preserveStartEnd';
  margin: {
    top: number;
    right: number;
    left: number;
    bottom: number;
  };
}

/**
 * Calculate responsive tick label configuration based on data length
 *
 * Automatically rotates and adjusts spacing for X-axis ticks based on the number
 * of data points. Also ensures sufficient bottom margin to prevent legend overlap.
 *
 * Philosophy: X-axis is a positioning guide, not meant to show everything.
 * Tooltip is the source of truth - users expect to probe, not read every label.
 *
 * Uses intelligent interval strategies:
 * - For small datasets (≤5): Show all labels, no rotation
 * - For medium datasets (6-8): Show all with slight rotation
 * - For larger datasets (>8): Aggressively skip labels - axis becomes visual guide
 *
 * @param dataLength - Number of data points in the chart
 * @param baseMargin - Base margin configuration (optional)
 * @returns TickConfig object with appropriate rotation, spacing, and margins
 */
export function calculateTickConfig(
  dataLength: number,
  baseMargin: { top: number; right: number; left: number; bottom: number } = { top: 5, right: 10, left: 10, bottom: 5 }
): TickConfig {
  // Add extra space for legend to prevent overlapping with tilted ticks
  const LEGEND_SPACING = 35;

  if (dataLength <= 5) {
    return {
      angle: 0,
      textAnchor: 'middle',
      fontSize: 12,
      interval: 0,
      margin: { ...baseMargin, bottom: baseMargin.bottom + LEGEND_SPACING }
    };
  } else if (dataLength <= 8) {
    return {
      angle: -15,
      textAnchor: 'end',
      fontSize: 11,
      interval: 0,
      margin: { ...baseMargin, bottom: Math.max(baseMargin.bottom, 20) + LEGEND_SPACING }
    };
  } else if (dataLength <= 12) {
    return {
      angle: -30,
      textAnchor: 'end',
      fontSize: 10,
      // Start skipping labels - show ~every 2nd label
      interval: Math.floor(dataLength / 6),
      margin: { ...baseMargin, bottom: Math.max(baseMargin.bottom, 30) + LEGEND_SPACING }
    };
  } else if (dataLength <= 20) {
    return {
      angle: -30,
      textAnchor: 'end',
      fontSize: 10,
      // Show ~every 3rd label for medium datasets
      interval: Math.floor(dataLength / 7),
      margin: { ...baseMargin, bottom: Math.max(baseMargin.bottom, 30) + LEGEND_SPACING }
    };
  } else {
    return {
      angle: -45,
      textAnchor: 'end',
      fontSize: 9,
      // Aggressive skipping for large datasets - show ~6-8 labels max
      interval: Math.floor(dataLength / 6),
      margin: { ...baseMargin, bottom: Math.max(baseMargin.bottom, 40) + LEGEND_SPACING }
    };
  }
}
